import React, { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';
import LyricConfirm from '../components/LyricConfirm';

/**
 * Admin → Salesforce API page.
 *
 * A test harness + per-app management tool for the deployed-app-facing
 * Salesforce API (server/routes/salesforceApi.js). By default it uses the
 * SAME shared service-account OAuth connection as the JIRA → Salesforce sync
 * (server/services/SalesforceAuthService.js).
 *
 * Unlike the AWS Logs Access tool, there is no per-app object/field scoping:
 * any app with a valid key can create/update any Salesforce object/field
 * ("open" scoping, by design — see session history). This is just: (1) a
 * live test-call harness, and (2) per-app enable/disable/rotate-key/apply,
 * mirroring AdminAwsLogsAccess.jsx.
 */

// Mask everything but a short prefix/suffix so a screen-share doesn't leak
// the working key.
function maskKey(key) {
    if (!key) return '';
    return `${key.slice(0, 9)}${'•'.repeat(Math.max(0, key.length - 13))}${key.slice(-4)}`;
}

function appUsageSnippet() {
    return `// Server-side only: do NOT expose this key in browser code or a static bundle.
const key = process.env.LYRIC_SALESFORCE_API_KEY;
// LYRIC_SALESFORCE_API_BASE_URL is auto-injected alongside the key for
// Lyric-hosted server runtimes. External services should set their own base URL.
const baseUrl = process.env.LYRIC_SALESFORCE_API_BASE_URL || '';
const headers = { Authorization: 'Bearer ' + key, 'Content-Type': 'application/json' };

// create a record on any object
await fetch(baseUrl + '/api/salesforce-api/records', {
  method: 'POST', headers,
  body: JSON.stringify({ object: 'Contact', fields: { LastName: 'Smith', Email: 'a@b.com' } }),
});

// update a field on an existing record
await fetch(baseUrl + '/api/salesforce-api/records/Contact/003XXXXXXXXXXXXAAA', {
  method: 'PATCH', headers,
  body: JSON.stringify({ fields: { Email: 'new@b.com' } }),
});

// update a Case's status by Case Number (no record ID lookup needed)
await fetch(baseUrl + '/api/salesforce-api/cases/00001234/status', {
  method: 'POST', headers,
  body: JSON.stringify({ status: 'Closed' }),
});`;
}

export default function AdminSalesforceApiAccess({ embedded = false } = {}) {
    const { oktaAuth } = useOktaAuth() || {};
    const [status, setStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [statusError, setStatusError] = useState('');

    // Live test-call harness (admin-only, bypasses the app-key auth).
    // Which Salesforce org the harness targets. 'default' is the shared
    // production connection; anything else is an admin-defined org stored in
    // AWS Secrets Manager (services/salesforceEnvironments.js).
    const [envName, setEnvName] = useState('default');
    const [environments, setEnvironments] = useState([]);
    const [loadingEnvs, setLoadingEnvs] = useState(true);
    const [envError, setEnvError] = useState('');
    const [canManageEnvs, setCanManageEnvs] = useState(false);
    const [showAddEnv, setShowAddEnv] = useState(false);
    const [savingEnv, setSavingEnv] = useState(false);
    const [envNotice, setEnvNotice] = useState('');
    const [verifyingEnv, setVerifyingEnv] = useState('');
    const [newEnv, setNewEnv] = useState({
        name: '', loginUrl: '', clientId: '', clientSecret: '', apiVersion: '',
    });

    const [testObject, setTestObject] = useState('Contact');
    const [testFields, setTestFields] = useState('{\n  "LastName": "Test",\n  "Email": "lyric-admin-test@example.com"\n}');
    const [testRecordId, setTestRecordId] = useState('');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testErrorMsg, setTestErrorMsg] = useState('');

    // Case finder: search for a Case, pick one, and load ALL of its current
    // field values into the object/fields editor above so any of them can be
    // reviewed and updated (not just Status).
    const [caseQuery, setCaseQuery] = useState('');
    const [caseResults, setCaseResults] = useState([]);
    const [caseSearching, setCaseSearching] = useState(false);
    const [caseSearchError, setCaseSearchError] = useState('');
    const [selectedCaseId, setSelectedCaseId] = useState('');
    const [loadingCaseRecord, setLoadingCaseRecord] = useState(false);
    // The full set of field values as loaded from Salesforce, so
    // runTestUpdate can submit only what the admin actually changed instead
    // of the whole record (which would otherwise include read-only fields
    // like CreatedDate/LastModifiedDate/SystemModstamp/CaseNumber and fail).
    const [originalCaseFields, setOriginalCaseFields] = useState(null);

    // Per-app "authenticate access" management (enable/disable/rotate the
    // developer-facing /api/salesforce-api/* key for one deployed app).
    const [apps, setApps] = useState([]);
    const [loadingApps, setLoadingApps] = useState(true);
    const [appsError, setAppsError] = useState('');
    const [expandedApp, setExpandedApp] = useState(null);
    const [appStatus, setAppStatus] = useState(null);
    const [loadingAppStatus, setLoadingAppStatus] = useState(false);
    const [appStatusError, setAppStatusError] = useState('');
    const [appBusy, setAppBusy] = useState(false);
    const [revealAppKey, setRevealAppKey] = useState(false);
    const [copiedApp, setCopiedApp] = useState('');
    const [confirmRotateApp, setConfirmRotateApp] = useState(false);
    const [appRedeployNeeded, setAppRedeployNeeded] = useState(false);
    const [redeployingApp, setRedeployingApp] = useState(false);
    const [appRedeployApplied, setAppRedeployApplied] = useState(false);

    const authHeader = useCallback(async () => {
        let token = '';
        try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [oktaAuth]);

    // Every harness call carries the selected org. 'default' is omitted so the
    // server falls through to the shared production connection.
    const envParam = useCallback(
        (separator = '?') => (envName && envName !== 'default'
            ? `${separator}environment=${encodeURIComponent(envName)}`
            : ''),
        [envName],
    );

    const loadStatus = useCallback(async () => {
        setLoadingStatus(true);
        setStatusError('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/admin/salesforce-api/status${envParam()}`, { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setStatus(body);
        } catch (e) {
            setStatusError(e.message);
        } finally {
            setLoadingStatus(false);
        }
    }, [authHeader, envParam]);

    const loadEnvironments = useCallback(async () => {
        setLoadingEnvs(true);
        setEnvError('');
        try {
            const headers = await authHeader();
            const res = await fetch('/api/admin/salesforce-api/environments', { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setEnvironments(body.environments || []);
            setCanManageEnvs(Boolean(body.canManage));
        } catch (e) {
            setEnvError(e.message);
        } finally {
            setLoadingEnvs(false);
        }
    }, [authHeader]);

    const saveEnvironment = useCallback(async () => {
        setSavingEnv(true);
        setEnvError('');
        setEnvNotice('');
        try {
            if (!newEnv.name.trim()) throw new Error('Name is required.');
            if (!newEnv.loginUrl.trim()) throw new Error('Login URL is required.');
            if (!newEnv.clientId.trim()) throw new Error('Consumer Key is required.');
            if (!newEnv.clientSecret.trim()) throw new Error('Consumer Secret is required.');
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/salesforce-api/environments', {
                method: 'POST', headers, body: JSON.stringify(newEnv),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            await loadEnvironments();
            setNewEnv({ name: '', loginUrl: '', clientId: '', clientSecret: '', apiVersion: '' });
            setShowAddEnv(false);
            // Saves take effect immediately — no restart — so switch to it.
            setEnvName(body.environment.name);
            setEnvNotice(`Saved "${body.environment.name}" — it is live now and selected below.`);
        } catch (e) {
            setEnvError(e.message);
        } finally {
            setSavingEnv(false);
        }
    }, [authHeader, newEnv, loadEnvironments]);

    const verifyEnvironment = useCallback(async (name) => {
        setVerifyingEnv(name);
        setEnvError('');
        setEnvNotice('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/admin/salesforce-api/environments/${encodeURIComponent(name)}/test`, {
                method: 'POST', headers,
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setEnvNotice(`"${name}" connected successfully — instance ${body.instanceUrl || 'unknown'}.`);
        } catch (e) {
            setEnvError(`"${name}" failed to connect: ${e.message}`);
        } finally {
            setVerifyingEnv('');
        }
    }, [authHeader]);

    const removeEnvironment = useCallback(async (name) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete the Salesforce environment "${name}"? Its stored Consumer Key and Secret will be removed.`)) return;
        setEnvError('');
        setEnvNotice('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/admin/salesforce-api/environments/${encodeURIComponent(name)}`, {
                method: 'DELETE', headers,
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            if (envName === name) setEnvName('default');
            await loadEnvironments();
            setEnvNotice(`Deleted "${name}".`);
        } catch (e) {
            setEnvError(e.message);
        }
    }, [authHeader, envName, loadEnvironments]);

    const loadApps = useCallback(async () => {
        setLoadingApps(true);
        setAppsError('');
        try {
            const headers = await authHeader();
            const res = await fetch('/api/deploy/list', { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setApps(body.apps || []);
        } catch (e) {
            setAppsError(e.message);
        } finally {
            setLoadingApps(false);
        }
    }, [authHeader]);

    useEffect(() => { loadStatus(); }, [loadStatus]);
    useEffect(() => { loadApps(); }, [loadApps]);
    useEffect(() => { loadEnvironments(); }, [loadEnvironments]);

    // Record Ids are org-specific, so anything loaded from the previous org is
    // meaningless — and dangerous — against the newly selected one. Clear the
    // case finder and the record under edit whenever the target org changes.
    useEffect(() => {
        setCaseResults([]);
        setCaseSearchError('');
        setSelectedCaseId('');
        setTestRecordId('');
        setOriginalCaseFields(null);
        setTestResult(null);
        setTestErrorMsg('');
    }, [envName]);


    const runTestCreate = useCallback(async () => {
        setTesting(true);
        setTestErrorMsg('');
        setTestResult(null);
        try {
            let fields;
            try { fields = JSON.parse(testFields); } catch { throw new Error('Fields must be valid JSON.'); }
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch(`/api/admin/salesforce-api/test-create${envParam()}`, {
                method: 'POST', headers, body: JSON.stringify({ object: testObject, fields }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setTestResult(body);
            if (body.recordId) setTestRecordId(body.recordId);
        } catch (e) {
            setTestErrorMsg(e.message);
        } finally {
            setTesting(false);
        }
    }, [authHeader, testObject, testFields, envParam]);

    const runTestUpdate = useCallback(async () => {
        setTesting(true);
        setTestErrorMsg('');
        setTestResult(null);
        try {
            if (!testRecordId.trim()) throw new Error('Record ID is required to test an update (create a record above first, or paste an existing one).');
            let fields;
            try { fields = JSON.parse(testFields); } catch { throw new Error('Fields must be valid JSON.'); }
            // When these fields were loaded from the Case editor above, submit
            // only what actually changed — the full record includes read-only
            // fields (CreatedDate, LastModifiedDate, SystemModstamp, the
            // auto-number CaseNumber, …) that Salesforce rejects on update.
            if (testObject === 'Case' && selectedCaseId && testRecordId.trim() === selectedCaseId && originalCaseFields) {
                const changed = {};
                for (const [key, value] of Object.entries(fields)) {
                    if (JSON.stringify(value) !== JSON.stringify(originalCaseFields[key])) changed[key] = value;
                }
                fields = changed;
                if (Object.keys(fields).length === 0) throw new Error('No fields were changed — edit a value above before updating.');
            }
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch(`/api/admin/salesforce-api/test-update${envParam()}`, {
                method: 'POST', headers, body: JSON.stringify({ object: testObject, recordId: testRecordId.trim(), fields }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setTestResult(body);
        } catch (e) {
            setTestErrorMsg(e.message);
        } finally {
            setTesting(false);
        }
    }, [authHeader, testObject, testRecordId, testFields, selectedCaseId, originalCaseFields, envParam]);

    const searchCases = useCallback(async () => {
        setCaseSearching(true);
        setCaseSearchError('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/admin/salesforce-api/cases?q=${encodeURIComponent(caseQuery.trim())}&limit=25${envParam('&')}`, { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setCaseResults(body.cases || []);
        } catch (e) {
            setCaseSearchError(e.message);
        } finally {
            setCaseSearching(false);
        }
    }, [authHeader, caseQuery, envParam]);


    // Loads every field of the selected Case into the object/fields editor
    // above so the admin can see (and change) any of them, not just Status.
    const selectCase = useCallback(async (caseRow) => {
        setSelectedCaseId(caseRow.id);
        setLoadingCaseRecord(true);
        setTestErrorMsg('');
        setTestResult(null);
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/admin/salesforce-api/records/Case/${encodeURIComponent(caseRow.id)}${envParam()}`, { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            // Drop Id/attributes: Id is passed separately as the Record ID and
            // Salesforce rejects updates that also include it in the body.
            const { attributes, Id, ...fields } = body.record || {};
            setTestObject('Case');
            setTestRecordId(caseRow.id);
            setTestFields(JSON.stringify(fields, null, 2));
            setOriginalCaseFields(fields);
        } catch (e) {
            setTestErrorMsg(e.message);
        } finally {
            setLoadingCaseRecord(false);
        }
    }, [authHeader, envParam]);


    const loadAppStatus = useCallback(async (appName) => {
        setLoadingAppStatus(true);
        setAppStatusError('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/apps/${encodeURIComponent(appName)}/salesforce`, { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setAppStatus(body);
        } catch (e) {
            setAppStatusError(e.message);
        } finally {
            setLoadingAppStatus(false);
        }
    }, [authHeader]);

    const toggleExpandApp = (appName) => {
        if (expandedApp === appName) {
            setExpandedApp(null);
            setAppStatus(null);
            return;
        }
        setExpandedApp(appName);
        setAppStatus(null);
        setAppStatusError('');
        setRevealAppKey(false);
        setAppRedeployNeeded(false);
        setAppRedeployApplied(false);
        loadAppStatus(appName);
    };

    // path: 'enable' | 'disable' | 'rotate-key'
    const appAct = useCallback(async (path) => {
        if (!expandedApp) return;
        setAppBusy(true);
        setAppStatusError('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch(`/api/apps/${encodeURIComponent(expandedApp)}/salesforce/${path}`, {
                method: 'POST', headers,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            await loadAppStatus(expandedApp);
            if (path === 'disable') {
                setAppRedeployNeeded(false);
                setAppRedeployApplied(false);
            } else {
                if (path === 'rotate-key') setRevealAppKey(true);
                setAppRedeployNeeded(!!data.redeployRequired);
                setAppRedeployApplied(false);
            }
        } catch (e) {
            setAppStatusError(e.message);
        } finally {
            setAppBusy(false);
        }
    }, [expandedApp, authHeader, loadAppStatus]);

    const applyAppRedeploy = useCallback(async () => {
        if (!expandedApp) return;
        setRedeployingApp(true);
        setAppStatusError('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/apps/${encodeURIComponent(expandedApp)}/salesforce/apply`, { method: 'POST', headers });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setAppRedeployNeeded(false);
            setAppRedeployApplied(true);
        } catch (e) {
            setAppStatusError(e.message);
        } finally {
            setRedeployingApp(false);
        }
    }, [expandedApp, authHeader]);

    const copyAppText = useCallback(async (text, what) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedApp(what);
            setTimeout(() => setCopiedApp(''), 1500);
        } catch { /* clipboard blocked — user can select manually */ }
    }, []);

    return (
        <div style={styles.page}>
            <div style={styles.pageHeader}>
                <h2 style={styles.title}>⚡ Salesforce API</h2>
            </div>
            <p style={styles.subtitle}>
                Test harness for the deployed-app-facing Salesforce API. This page proves the
                connection works and lets you hand a per-app key to a team.
                Scoping is intentionally open: any app with a valid key can create/update any object or field.
            </p>

            {/* The New Dashboard sidebar already provides admin navigation —
                showing this dropdown too would be redundant there, and its
                links do a full-route navigate() that would exit the inline
                tool pane. Only render it when this page is opened directly
                (e.g. via /admin/salesforce-api). */}
            {!embedded && <AdminMenu />}

            <div style={styles.section}>
                <h3 style={styles.h3}>Salesforce environment</h3>
                <p style={styles.help}>
                    Which org the test calls below run against. <b>Production</b> is Lyric&apos;s shared
                    service-account connection (also used by the JIRA&nbsp;→&nbsp;Salesforce sync and by
                    deployed apps). Add a sandbox to try the same calls somewhere safe — deployed apps
                    always keep using Production regardless of what you pick here.
                </p>
                {envError && <div style={styles.error}>{envError}</div>}
                {envNotice && <div style={styles.auditWarn}>{envNotice}</div>}

                <div style={styles.actions}>
                    <select
                        style={{ ...styles.input, width: 'auto', minWidth: '260px' }}
                        value={envName}
                        onChange={(e) => setEnvName(e.target.value)}
                        disabled={loadingEnvs}
                    >
                        <option value="default">Production (shared connection)</option>
                        {environments.map((env) => (
                            <option key={env.name} value={env.name}>
                                {env.name} — {env.loginUrl}
                            </option>
                        ))}
                    </select>
                    {canManageEnvs && (
                        <button type="button" style={styles.btn} onClick={() => setShowAddEnv((v) => !v)}>
                            {showAddEnv ? 'Cancel' : '+ Add environment'}
                        </button>
                    )}
                    {envName !== 'default' && canManageEnvs && (
                        <>
                            <button
                                type="button"
                                style={styles.btn}
                                disabled={verifyingEnv === envName}
                                onClick={() => verifyEnvironment(envName)}
                            >
                                {verifyingEnv === envName ? 'Testing…' : 'Test connection'}
                            </button>
                            <button type="button" style={styles.btnDanger} onClick={() => removeEnvironment(envName)}>
                                Delete
                            </button>
                        </>
                    )}
                </div>

                {loadingEnvs && <p style={styles.help}>Loading environments…</p>}
                {!loadingEnvs && !canManageEnvs && environments.length === 0 && (
                    <p style={styles.help}>No additional environments. Only a full Lyric admin can add one.</p>
                )}

                {showAddEnv && canManageEnvs && (
                    <div style={styles.tlsBlock}>
                        <p style={styles.help}>
                            Create a Connected App in the target org with the <b>client credentials</b> flow enabled,
                            then paste its Consumer Key and Secret here. Saved credentials go straight to AWS Secrets
                            Manager and take effect immediately — no restart or redeploy.
                        </p>
                        <table style={styles.table}>
                            <tbody>
                                <tr>
                                    <td style={styles.td}><b>Name</b></td>
                                    <td style={styles.td}>
                                        <input
                                            style={styles.input}
                                            value={newEnv.name}
                                            onChange={(e) => setNewEnv({ ...newEnv, name: e.target.value })}
                                            placeholder="UAT"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={styles.td}><b>Login URL</b></td>
                                    <td style={styles.td}>
                                        <input
                                            style={styles.input}
                                            value={newEnv.loginUrl}
                                            onChange={(e) => setNewEnv({ ...newEnv, loginUrl: e.target.value })}
                                            placeholder="https://ischemaview--uat.sandbox.my.salesforce.com"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={styles.td}><b>Consumer Key</b></td>
                                    <td style={styles.td}>
                                        <input
                                            style={styles.input}
                                            value={newEnv.clientId}
                                            onChange={(e) => setNewEnv({ ...newEnv, clientId: e.target.value })}
                                            placeholder="3MVG9..."
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={styles.td}><b>Consumer Secret</b></td>
                                    <td style={styles.td}>
                                        <input
                                            style={styles.input}
                                            type="password"
                                            autoComplete="new-password"
                                            value={newEnv.clientSecret}
                                            onChange={(e) => setNewEnv({ ...newEnv, clientSecret: e.target.value })}
                                            placeholder="Never shown again once saved"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={styles.td}><b>API version</b></td>
                                    <td style={styles.td}>
                                        <input
                                            style={styles.input}
                                            value={newEnv.apiVersion}
                                            onChange={(e) => setNewEnv({ ...newEnv, apiVersion: e.target.value })}
                                            placeholder="v60.0 (optional)"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div style={styles.actions}>
                            <button type="button" style={styles.btnPrimary} disabled={savingEnv} onClick={saveEnvironment}>
                                {savingEnv ? 'Saving…' : 'Save environment'}
                            </button>
                        </div>
                    </div>
                )}

                {environments.length > 0 && (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Name</th>
                                <th style={styles.th}>Login URL</th>
                                <th style={styles.th}>Consumer Key</th>
                                <th style={styles.th}>API</th>
                                <th style={styles.th}>Added by</th>
                                {canManageEnvs && <th style={styles.th} />}
                            </tr>
                        </thead>
                        <tbody>
                            {environments.map((env) => (
                                <tr key={env.name}>
                                    <td style={styles.td}>{env.name}</td>
                                    <td style={styles.td}>{env.loginUrl}</td>
                                    <td style={styles.td}>{env.clientIdPreview || '—'}</td>
                                    <td style={styles.td}>{env.apiVersion}</td>
                                    <td style={styles.td}>{env.addedBy || '—'}</td>
                                    {canManageEnvs && (
                                        <td style={styles.td}>
                                            <button
                                                type="button"
                                                style={styles.btn}
                                                disabled={verifyingEnv === env.name}
                                                onClick={() => verifyEnvironment(env.name)}
                                            >
                                                {verifyingEnv === env.name ? 'Testing…' : 'Test'}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>Connection status</h3>
                {loadingStatus ? <p style={styles.help}>Loading…</p> : statusError ? (
                    <div style={styles.error}>{statusError}</div>
                ) : status && (
                    <table style={styles.table}>
                        <tbody>
                            <tr><td style={styles.td}><b>Configured</b></td><td style={styles.td}>
                                <span style={status.configured ? styles.badgeOk : styles.badgeFail}>
                                    {status.configured ? 'Yes' : 'No'}
                                </span>
                            </td></tr>
                            <tr><td style={styles.td}><b>Auth flow</b></td><td style={styles.td}>{status.flow || '—'}</td></tr>
                            <tr><td style={styles.td}><b>Instance URL</b></td><td style={styles.td}>{status.instanceUrl || '—'}</td></tr>
                            <tr><td style={styles.td}><b>API version</b></td><td style={styles.td}>{status.apiVersion || '—'}</td></tr>
                        </tbody>
                    </table>
                )}
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>Live test call <IntegrationCodeInfo /></h3>
                <p style={styles.help}>
                    These hit the LIVE Salesforce org (no mocking) using Lyric&apos;s own service-account
                    connection — use test/sandbox data. Currently targeting{' '}
                    <b>{envName === 'default' ? 'Production (shared connection)' : envName}</b>.
                </p>
                {testErrorMsg && <div style={styles.error}>{testErrorMsg}</div>}

                <div style={{ marginBottom: '16px', borderBottom: '1px solid #2f3a44', paddingBottom: '12px' }}>
                    <label style={styles.help}>Find a Case (leave blank to see the most recently modified)</label>
                    <div style={styles.actions}>
                        <input
                            style={styles.input}
                            value={caseQuery}
                            onChange={(e) => setCaseQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') searchCases(); }}
                            placeholder="Case Number, Subject, or Contact/Account name"
                        />
                        <button type="button" style={styles.btn} disabled={caseSearching} onClick={searchCases}>
                            {caseSearching ? 'Searching…' : 'Search Cases'}
                        </button>
                    </div>
                    {caseSearchError && <div style={styles.error}>{caseSearchError}</div>}
                    {caseResults.length > 0 && (
                        <table style={styles.table}>
                            <thead>
                                <tr><th style={styles.td}>Case #</th><th style={styles.td}>Subject</th><th style={styles.td}>Status</th><th style={styles.td} /></tr>
                            </thead>
                            <tbody>
                                {caseResults.map((c) => (
                                    <tr key={c.id}>
                                        <td style={styles.td}>{c.caseNumber}</td>
                                        <td style={styles.td}>{c.subject}</td>
                                        <td style={styles.td}>{c.status}</td>
                                        <td style={styles.td}>
                                            <button
                                                type="button"
                                                style={styles.btn}
                                                disabled={loadingCaseRecord && selectedCaseId === c.id}
                                                onClick={() => selectCase(c)}
                                            >
                                                {loadingCaseRecord && selectedCaseId === c.id ? 'Loading…' : 'View / edit all fields'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={styles.actions}>
                    <label style={styles.help}>Object</label>
                    <input style={styles.input} value={testObject} onChange={(e) => setTestObject(e.target.value)} placeholder="Contact" />
                </div>
                <div style={{ marginTop: '8px' }}>
                    <label style={styles.help}>Fields (JSON) — selecting a Case above loads every one of its current values here</label>
                    <textarea
                        style={{ ...styles.input, minHeight: '90px', fontFamily: 'monospace' }}
                        value={testFields}
                        onChange={(e) => setTestFields(e.target.value)}
                    />
                </div>
                <div style={styles.actions}>
                    <button type="button" style={styles.btnPrimary} disabled={testing} onClick={runTestCreate}>
                        {testing ? 'Working…' : 'Create record'}
                    </button>
                    <input
                        style={{ ...styles.input, maxWidth: '260px' }}
                        value={testRecordId}
                        onChange={(e) => setTestRecordId(e.target.value)}
                        placeholder="Record ID to update (e.g. from a create or Case search above)"
                    />
                    <button type="button" style={styles.btn} disabled={testing} onClick={runTestUpdate}>
                        {testing ? 'Working…' : 'Update record'}
                    </button>
                </div>

                {testResult && (
                    <pre style={styles.pre}>{JSON.stringify(testResult, null, 2)}</pre>
                )}
            </div>


            <div style={styles.section}>
                <h3 style={styles.h3}>Deployed apps — authenticate access</h3>
                <p style={styles.help}>
                    Grant one deployed app its own scoped key to call the developer-facing{' '}
                    <code>/api/salesforce-api/*</code> relay — rate-limited and fully audited. Lyric
                    auto-injects the key as <code>LYRIC_SALESFORCE_API_KEY</code> into the app&apos;s
                    server process on its next (re)deploy; the app itself never sees or handles real Salesforce
                    credentials. Use it only from server-side code — not from a browser bundle or static frontend.
                    There is no per-app object/field restriction — any enabled app can create/update any Salesforce object.
                </p>
                {appsError && <div style={styles.error}>{appsError}</div>}
                {loadingApps ? <p style={styles.help}>Loading apps…</p> : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>App</th>
                                <th style={styles.th}>Owner</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Salesforce API</th>
                                <th style={styles.th} />
                            </tr>
                        </thead>
                        <tbody>
                            {apps.map((a) => (
                                <React.Fragment key={a.appName}>
                                    <tr>
                                        <td style={styles.td}>{a.displayName || a.appName}</td>
                                        <td style={styles.td}>{a.deployedBy}</td>
                                        <td style={styles.td}>{a.status}</td>
                                        <td style={styles.td}>
                                            {expandedApp === a.appName && appStatus ? (
                                                <span style={appStatus.salesforceEnabled ? styles.badgeOk : styles.badgeFail}>
                                                    {appStatus.salesforceEnabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td style={styles.td}>
                                            <button type="button" style={styles.btn} onClick={() => toggleExpandApp(a.appName)}>
                                                {expandedApp === a.appName ? 'Close' : 'Manage'}
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedApp === a.appName && (
                                        <tr>
                                            <td style={styles.td} colSpan={5}>
                                                {loadingAppStatus ? <p style={styles.help}>Loading…</p> : appStatusError ? (
                                                    <div style={styles.error}>{appStatusError}</div>
                                                ) : appStatus && (
                                                    <div style={styles.tlsBlock}>
                                                        <div style={styles.actions}>
                                                            {!appStatus.salesforceEnabled ? (
                                                                <button type="button" style={styles.btnPrimary} disabled={appBusy} onClick={() => appAct('enable')}>
                                                                    {appBusy ? 'Enabling…' : 'Enable Salesforce API'}
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <button type="button" style={styles.btn} disabled={appBusy} onClick={() => setConfirmRotateApp(true)}>
                                                                        Rotate key
                                                                    </button>
                                                                    <button type="button" style={styles.btnDanger} disabled={appBusy} onClick={() => appAct('disable')}>
                                                                        {appBusy ? 'Disabling…' : 'Disable'}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>

                                                        {appStatus.salesforceEnabled && appStatus.apiKey && (
                                                            <>
                                                                <div style={{ marginTop: '10px' }}>
                                                                    <div style={styles.help}>
                                                                        API key (auto-injected as <code>LYRIC_SALESFORCE_API_KEY</code> into the server runtime)
                                                                    </div>
                                                                    <div style={styles.actions}>
                                                                        <code style={styles.codeInline}>
                                                                            {revealAppKey ? appStatus.apiKey : maskKey(appStatus.apiKey)}
                                                                        </code>
                                                                        <button type="button" style={styles.btn} onClick={() => setRevealAppKey((v) => !v)}>
                                                                            {revealAppKey ? 'Hide' : 'Reveal'}
                                                                        </button>
                                                                        <button type="button" style={styles.btn} onClick={() => copyAppText(appStatus.apiKey, 'key')}>
                                                                            {copiedApp === 'key' ? 'Copied!' : 'Copy'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div style={{ marginTop: '10px' }}>
                                                                    <div style={styles.help}>Usage snippet for the developer</div>
                                                                    <div style={styles.actions}>
                                                                        <button type="button" style={styles.btn} onClick={() => copyAppText(appUsageSnippet(), 'snippet')}>
                                                                            {copiedApp === 'snippet' ? 'Copied!' : 'Copy snippet'}
                                                                        </button>
                                                                    </div>
                                                                    <pre style={styles.pre}>{appUsageSnippet()}</pre>
                                                                </div>
                                                            </>
                                                        )}

                                                        {appRedeployNeeded && (
                                                            <div style={styles.auditWarn}>
                                                                Redeploy <b>{a.displayName || a.appName}</b> to apply the key to the running app.
                                                                <button
                                                                    type="button"
                                                                    style={{ ...styles.btnPrimary, marginLeft: '10px' }}
                                                                    disabled={redeployingApp}
                                                                    onClick={applyAppRedeploy}
                                                                >
                                                                    {redeployingApp ? 'Redeploying…' : 'Redeploy now'}
                                                                </button>
                                                            </div>
                                                        )}
                                                        {appRedeployApplied && !appRedeployNeeded && (
                                                            <div style={styles.help}>🚀 Redeploy started — the key is injected on respawn.</div>
                                                        )}
                                                        {appStatus.rateLimit && (
                                                            <div style={styles.help}>
                                                                Limit: <b>{appStatus.rateLimit.max}</b> / {Math.round((appStatus.rateLimit.windowMs || 0) / 60000)} min
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {!apps.length && (
                                <tr><td style={styles.td} colSpan={5}>No deployed apps found.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <LyricConfirm
                isOpen={confirmRotateApp}
                tone="danger"
                title="Rotate this app's API key?"
                message="The current key stops working immediately. The app must redeploy to pick up the new one — anything using the old key will get 401s until then."
                confirmLabel="Rotate key"
                onConfirm={() => { setConfirmRotateApp(false); appAct('rotate-key'); }}
                onCancel={() => setConfirmRotateApp(false)}
            />
        </div>
    );
}

// Small "i" popover showing the exact server-side HTTP calls an external app
// makes to use this API with its own per-app key — so an admin can hand this
// straight to a team without writing the integration snippet themselves.
function IntegrationCodeInfo() {
    const [open, setOpen] = useState(false);
    const baseUrl = (typeof window !== 'undefined' && window.location && window.location.origin) || 'https://<your-lyric-host>';

    const curlCreate = `curl -X POST ${baseUrl}/api/salesforce-api/records \\
  -H "Authorization: Bearer $LYRIC_SALESFORCE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"object": "Contact", "fields": {"LastName": "Doe", "Email": "doe@example.com"}}'`;

    const curlUpdate = `curl -X PATCH ${baseUrl}/api/salesforce-api/records/Case/500XXXXXXXXXXXXXXX \\
  -H "Authorization: Bearer $LYRIC_SALESFORCE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"fields": {"Status": "Closed"}}'`;

    const curlCaseStatus = `curl -X POST ${baseUrl}/api/salesforce-api/cases/00001234/status \\
  -H "Authorization: Bearer $LYRIC_SALESFORCE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"status": "Closed"}'`;

    const jsExample = `// Server-side only (Node / backend code), never in the browser.
const res = await fetch('${baseUrl}/api/salesforce-api/records', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.LYRIC_SALESFORCE_API_KEY}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    object: 'Contact',
    fields: { LastName: 'Doe', Email: 'doe@example.com' },
  }),
});
const data = await res.json();`;

    return (
        <span style={{ position: 'relative', display: 'inline-flex', marginLeft: '8px' }}>
            <button
                type="button"
                aria-label="Show the server-side code an external app uses to call this API"
                title="Show the server-side code an external app uses to call this API"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
                style={{
                    width: '18px', height: '18px', padding: 0, borderRadius: '50%',
                    border: '1px solid #888', background: 'transparent', color: '#bbb',
                    fontSize: '12px', fontWeight: 700, lineHeight: '16px', cursor: 'pointer',
                }}
            >
                i
            </button>
            {open && (
                <div
                    role="tooltip"
                    style={{
                        position: 'absolute', zIndex: 5, top: '24px', left: 0, width: '480px',
                        maxHeight: '460px', overflowY: 'auto', padding: '12px 14px',
                        border: '1px solid #555', borderRadius: '6px', background: '#1a1e23',
                        color: '#ddd', fontSize: '12px', lineHeight: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <strong style={{ color: '#4fc3f7' }}>Calling this from server-side app code</strong>
                        <button type="button" onClick={() => setOpen(false)} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                    </div>
                    <p style={{ margin: '8px 0' }}>
                        Get a key for the app from &quot;Deployed apps — authenticate access&quot; below
                        (Enable → copy the key), then set it as an env var
                        (e.g. <code style={styles.codeInline}>LYRIC_SALESFORCE_API_KEY</code>) in that app&apos;s
                        backend/server runtime. No Salesforce credentials are ever given to the app itself — Lyric brokers the call.
                        Do not expose this key in browser code or a static frontend build.
                    </p>
                    <p style={{ margin: '10px 0 2px', color: '#aaa' }}>Create a record:</p>
                    <pre style={styles.pre}>{curlCreate}</pre>
                    <p style={{ margin: '10px 0 2px', color: '#aaa' }}>Update a record by Id:</p>
                    <pre style={styles.pre}>{curlUpdate}</pre>
                    <p style={{ margin: '10px 0 2px', color: '#aaa' }}>Update a Case&apos;s status by Case Number:</p>
                    <pre style={styles.pre}>{curlCaseStatus}</pre>
                    <p style={{ margin: '10px 0 2px', color: '#aaa' }}>From Node/JavaScript server code:</p>
                    <pre style={styles.pre}>{jsExample}</pre>
                    <p style={{ margin: '8px 0 0', color: '#888' }}>
                        Also accepts <code style={styles.codeInline}>X-Lyric-Api-Key</code> instead of the
                        Authorization header. Each app is rate-limited; see the connection status panel above.
                    </p>
                </div>
            )}
        </span>
    );
}

const styles = {
    page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#fff' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' },
    title: { margin: 0, color: '#4fc3f7' },
    subtitle: { color: '#aaa', marginTop: 0, lineHeight: 1.5 },
    section: { marginTop: '20px', padding: '16px', borderRadius: '6px', background: '#1f2429', border: '1px solid #2f3a44' },
    h3: { marginTop: 0, marginBottom: '8px', color: '#4fc3f7' },
    help: { color: '#888', fontSize: '0.85rem', marginTop: 0 },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '8px' },
    th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #2f3a44', color: '#aaa', fontWeight: 600, fontSize: '0.85rem' },
    td: { padding: '6px 8px', verticalAlign: 'top', borderBottom: '1px solid #232a31' },
    input: {
        padding: '6px 8px', borderRadius: '4px', border: '1px solid #444',
        background: '#161a1e', color: '#fff', width: '100%', boxSizing: 'border-box', fontSize: '13px',
    },
    btn: { padding: '8px 14px', background: '#2f3a44', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    btnPrimary: { padding: '8px 14px', background: '#0052cc', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 },
    btnDanger: { padding: '6px 10px', background: '#5a1a1a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' },
    actions: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' },
    error: { padding: '8px 12px', background: '#4a1a1a', border: '1px solid #ef5350', borderRadius: '4px', color: '#ffcdd2', margin: '8px 0' },
    auditWarn: {
        marginTop: '8px', padding: '6px 10px', background: '#4a3a1a', border: '1px solid #ffb74d',
        borderRadius: '4px', color: '#ffe0b2', fontSize: '12px',
    },
    badgeOk: { display: 'inline-block', padding: '2px 8px', background: '#1a4a1a', color: '#c8e6c9', border: '1px solid #4caf50', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
    badgeFail: { display: 'inline-block', padding: '2px 8px', background: '#4a1a1a', color: '#ffcdd2', border: '1px solid #ef5350', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
    tlsBlock: { marginTop: '12px', padding: '10px 12px', background: '#0d1117', border: '1px solid #2f3a44', borderRadius: '4px', fontSize: '13px' },
    codeInline: { padding: '4px 8px', background: '#161a1e', border: '1px solid #2f3a44', borderRadius: '4px', fontSize: '12px', wordBreak: 'break-all' },
    pre: {
        marginTop: '6px', padding: '10px 12px', background: '#161a1e', border: '1px solid #2f3a44',
        borderRadius: '4px', fontSize: '12px', overflowX: 'auto', whiteSpace: 'pre',
    },
};
